import { Box, ButtonBase, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { tokens } from "../../../../theme";
import { useReportHubFilters } from "../ReportHubFilterContext";
import { getReportForEnvironment, getReportKey } from "../reports";
import SHModal from "../../../theme/Modal/SHModal";
import Subheading from "../../../theme/text/Subheading";
import { DropdownBaseV2 } from "../../../theme/dropdowns/Dropdown";

import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import { ReportFilterDropdownFilter } from "./filter/ReportFilterDropdownFilter";
import { useMemo } from "react";
import { reportFilterComponentTypes } from "../chartFilterEnums";
import { ReportFilterBooleanFilter } from "./filter/ReportFilterBooleanFilter";
import { ReportFilterSearchQueryDropdownFilter } from "./filter/ReportFilterSearchQueryDropdownFilter";
import { ReportFilterCountryFilter } from "./filter/ReportFilterCountryFilter";

export const MRhFilter = (props) => {
    const { t, ready,i18n } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const handleClose = () => {
        props.handleClose();
    }

    const filtersRegistry = useReportHubFilters();
    const chartOption = getReportForEnvironment(getReportKey(props.currentReport), filtersRegistry.environment);
    const usedFilters = props.currentReport ? Object.keys(props.currentReport.filter || {}) : [];
    const availableFilters = useMemo(() => {
        if (!props.currentReport) return []; 
        if(!props.currentReport.filter) return [];

        const used = Object.keys(props.currentReport.filter);
        return chartOption?.filters.filter(f => !used.includes(f)) || [];
    }, [chartOption?.filters, props.currentReport]);

    return (
        <SHModal
            open={props.open}
            onClose={handleClose}
        >
            <Box 
                sx={{
                    textAlign:"center",
                    paddingY:3,
                    paddingX:3,
                    display:"flex",
                    flexDirection:"column",
                    gap:1
                }}
            >
                <Subheading>{t("filter")}</Subheading>

                {/* Button to display more possible filters */}
                {availableFilters?.length > 0 &&
                    <DropdownBaseV2
                        onChange={(selected) => {
                            const filterKey = availableFilters[selected];;
                            const registryItem = typeof filtersRegistry.filters[filterKey] === "function"
                                ? filtersRegistry.filters[filterKey]()
                                : filtersRegistry.filters[filterKey];

                            let value;
                            switch(registryItem?.componentType) {
                                case reportFilterComponentTypes.COUNTRIES:
                                case reportFilterComponentTypes.SEARCHQUERYDROPDOWN:
                                case reportFilterComponentTypes.DROPDOWN:
                                    value = [];
                                    break;
                                case reportFilterComponentTypes.BOOLEAN:
                                    value = [false];
                                    break;
                            }
                            props.setCurrentReport((prev) => {

                                return {
                                    ...prev,
                                    filter: {
                                        ...prev.filter,
                                        [filterKey]: {
                                            value: value,
                                            primary: false, 
                                        }
                                    }
                                };
                            });
                        }}
                        useTranslation
                        options={availableFilters}
                        selected={-1}
                        closeOnSelection
                        resetOnSelection
                    >
                        {({anchorEl,onClick}) => (
                            <ButtonBase
                                onClick={onClick}
                                sx={{
                                    width:1,
                                    justifyContent:'space-between',
                                    display:'flex',
                                    pr:1,
                                    height:32,
                                    flexShrink:0,
                                    borderRadius:4,
                                    background: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[200],
                                    color:theme.palette.mode === "dark" ? colors.txt["primary"] : colors.txt["primary"],
                                    fontSize:14,
                                    fontFamily:"Roboto,sans-serif",
                                    fontWeight:500,

                                    "@media screen and (max-width: 1024px)" : {
                                        ml:"auto",
                                    },
                                }}
                            >
                                <Box sx={{
                                    width:32,
                                }}/>

                                {t('add_filter')}
                                   
                                <AddOutlinedIcon fontSize="small"/>
                            </ButtonBase>
                        )}
                    </DropdownBaseV2>
                }
                {/* <Text light>The data presented will automatically be filtered through the selected warehouses</Text> */}
            </Box>
            <Box
                sx={{
                    background:theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                    padding:3,
                    borderTopLeftRadius:16,
                    borderTopRightRadius:16,
                    display:"flex",
                    flexDirection:"column",
                    gap:2
                }}
            >

                
                {usedFilters.map((key, idx) => {
                    const filterConfig = typeof filtersRegistry.filters[key] === "function"
                        ? filtersRegistry.filters[key]()
                        : filtersRegistry.filters[key];
    
                    return filterConfig?.componentType === reportFilterComponentTypes.DROPDOWN ? (
                        <ReportFilterDropdownFilter
                            wrapFilters={props.wrapFilters}
                            key={key}
                            fKey={key}
                            currentReport={props.currentReport}
                            setCurrentReport={props.setCurrentReport}
                            filter={props.currentReport?.filter[key]}
                        />
                    ) : filterConfig?.componentType === reportFilterComponentTypes.COUNTRIES ? (
                        <ReportFilterCountryFilter
                            wrapFilters={props.wrapFilters}
                            key={key}
                            fKey={key}
                            currentReport={props.currentReport}
                            setCurrentReport={props.setCurrentReport}
                            filter={props.currentReport?.filter[key]}
                        />
                    ) : filterConfig?.componentType === reportFilterComponentTypes.SEARCHQUERYDROPDOWN ? (
                        <ReportFilterSearchQueryDropdownFilter
                            wrapFilters={props.wrapFilters}
                            key={key}
                            fKey={key}
                            currentReport={props.currentReport}
                            setCurrentReport={props.setCurrentReport}
                            filter={props.currentReport?.filter[key]}
                        />
                    ) : filterConfig?.componentType === reportFilterComponentTypes.BOOLEAN ?
                        <ReportFilterBooleanFilter
                            wrapFilters={props.wrapFilters}
                            key={key}
                            fKey={key}
                            currentReport={props.currentReport}
                            setCurrentReport={props.setCurrentReport}
                            filter={props.currentReport?.filter[key]}
                        />
                    : 
                        null
                    
                })}

                <ButtonBase
                    onClick={handleClose}
                    sx={{
                        mt:1,
                        width:1,
                        textAlign:"center",
                        lineHeight:'32px',
                        background:theme.palette.mode === "dark" ? colors.txt["secondary"] : colors.grey[400],
                        color:theme.palette.mode === "dark" ? colors.txt['primary'] : colors.txt['light'],
                        borderRadius:4,
                        fontWeight:"bold",
                        fontSize:14,
                        fontFamily:"Roboto,sans-serif",
                    }}
                >
                    {t("close")}
                </ButtonBase>
                
            </Box>

        </SHModal>
    )
}
